import * as Sentry from '@sentry/react-router'

// Carried on the feedback event as the `feedback_entry_point` tag. The production-health sweep treats a
// report filed from an error screen as a defect by construction and one opened from a menu as unclassified.
export type FeedbackEntryPoint = 'action-error-dialog' | 'error-page' | 'sidebar' | 'top-nav-menu'

export async function openSentryFeedback({
  entryPoint,
  linkedErrorEventId
}: {
  entryPoint: FeedbackEntryPoint
  linkedErrorEventId?: string
}) {
  const replay = Sentry.getReplay()
  await replay?.flush()

  try {
    const feedback = Sentry.getFeedback()
    if (feedback) {
      const stopAnnotatingFeedback = annotateNextFeedbackEvent({ entryPoint, linkedErrorEventId })
      let form: Awaited<ReturnType<typeof feedback.createForm>> | undefined
      try {
        // Cancel fires onFormClose; a successful submit fires onFormSubmitted instead.
        form = await feedback.createForm({ onFormClose: stopAnnotatingFeedback, onFormSubmitted: stopAnnotatingFeedback })
        form.appendToDom()

        // On error pages the Sentry shadow-DOM host is destroyed by React's error
        // boundary re-render. appendToDom() silently no-ops in that case, so we
        // detect the failure and fall through to the prompt-based fallback.
        if ((form.el as HTMLElement)?.isConnected) {
          form.open()
          return
        }
      } catch {
        // Fall through to prompt-based fallback below.
      }

      stopAnnotatingFeedback()
      if (form) {
        try {
          form.removeFromDom()
        } catch {
          // ignore cleanup errors
        }
      }
    }

    // Fallback: collect feedback via a simple browser prompt.
    const message = window.prompt('Describe the issue you encountered:')
    if (!message) return

    const user = Sentry.getCurrentScope().getUser()
    Sentry.captureFeedback({
      message,
      name: user?.feedbackDisplayName ?? user?.fullName ?? user?.username,
      email: user?.email,
      associatedEventId: linkedErrorEventId,
      tags: { feedback_entry_point: entryPoint }
    })
  } finally {
    // flush() transitions out of buffer mode; restart so subsequent
    // feedback submissions still include replay data.
    replay?.startBuffering()
  }
}

// The widget form cannot link an error event, and its `tags` option is written onto the shared scope, where
// it would stick to every later error in the session. The pre-send hook touches only the feedback event.
function annotateNextFeedbackEvent({
  entryPoint,
  linkedErrorEventId
}: {
  entryPoint: FeedbackEntryPoint
  linkedErrorEventId?: string
}): () => void {
  const client = Sentry.getClient()
  if (!client) return () => {}
  return client.on('beforeSendFeedback', (feedbackEvent) => {
    feedbackEvent.tags = { ...feedbackEvent.tags, feedback_entry_point: entryPoint }
    if (linkedErrorEventId) feedbackEvent.contexts.feedback.associated_event_id = linkedErrorEventId
  })
}
